import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, Plus, Pencil, Trash2 } from 'lucide-react';
import { getUsers, createUser, updateUser, deleteUser, getClinics } from '../../services/api';
import { toast } from 'sonner';

const validatePhone = (p) => /^05\d{9}$/.test(p);
const roleLabel = { systemadmin: 'Sistem Yöneticisi', clinic_owner: 'Klinik Sahibi', vet: 'Veteriner', clinic_vet: 'Veteriner', clinic_tech: 'Teknisyen', clinic_staff: 'Personel' };
const roleColor = { systemadmin: 'bg-red-100 text-red-600', clinic_owner: 'bg-purple-100 text-purple-600', vet: 'bg-emerald-100 text-emerald-700', clinic_vet: 'bg-emerald-100 text-emerald-700', clinic_tech: 'bg-orange-100 text-orange-600', clinic_staff: 'bg-gray-100 text-gray-600' };

export const AdminUsersPanel = () => {
  const [users, setUsers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userModal, setUserModal] = useState(null);
  const [userForm, setUserForm] = useState({ name: '', phone: '', email: '', role: '', clinic_ids: [], password: '' });

  useEffect(() => {
    getUsers().then(r => setUsers(r.data)).catch(() => {});
    getClinics().then(r => setClinics(r.data)).catch(() => {});
  }, []);

  const openUserEdit = (u) => {
    const clinicIds = u.clinic_ids || (u.clinic_id ? [u.clinic_id] : []);
    setUserForm({ name: u.name || '', phone: u.phone || '', email: u.email || '', role: u.role || '', clinic_ids: clinicIds, password: '' });
    setUserModal(u);
  };

  const handleSaveUser = async () => {
    if (!validatePhone(userForm.phone)) { toast.error('Telefon 05 ile başlamalı ve 11 haneli olmalı'); return; }
    try {
      if (userModal?.id) {
        const data = { ...userForm };
        if (!data.password) delete data.password;
        await updateUser(userModal.id, data);
        toast.success('Kullanıcı güncellendi');
      } else {
        await createUser(userForm);
        toast.success('Kullanıcı oluşturuldu');
      }
      setUserModal(null);
      getUsers().then(r => setUsers(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || 'Hata'); }
  };

  const toggleClinicInForm = (clinicId) => {
    setUserForm(prev => {
      const ids = prev.clinic_ids || [];
      return ids.includes(clinicId)
        ? { ...prev, clinic_ids: ids.filter(id => id !== clinicId) }
        : { ...prev, clinic_ids: [...ids, clinicId] };
    });
  };

  const filtered = users.filter(u => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) || (u.phone || '').includes(q) || (u.clinic_names || []).some(cn => cn.name.toLowerCase().includes(q));
  });

  return (
    <>
      <div className="space-y-1.5">
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" />
            <input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Ad, telefon veya klinik ara..."
              className="w-full h-10 bg-[#F2F2F7] rounded-xl pl-9 pr-3 text-sm outline-none" data-testid="user-search-input" />
          </div>
          <Button size="sm" onClick={() => { setUserForm({ name: '', phone: '', email: '', role: 'clinic_owner', clinic_ids: [], password: '' }); setUserModal({}); }}
            className="bg-[#6b6de3] rounded-full text-xs" data-testid="add-user-btn">
            <Plus size={14} className="mr-1" /> Kullanıcı Ekle
          </Button>
        </div>
        {filtered.map(u => (
          <div key={u.id} className="bg-white rounded-xl px-3 py-2 shadow-sm flex items-center gap-3" data-testid={`user-${u.id}`}>
            <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-sm text-gray-900">{u.name}</p>
              <p className="text-xs text-[#8E8E93]">{u.phone}</p>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${roleColor[u.role] || 'bg-gray-100 text-gray-600'}`}>{roleLabel[u.role] || u.role}</span>
              {(u.clinic_names || []).map(cn => (
                <span key={cn.id} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-[#6b6de3] font-medium">{cn.name}</span>
              ))}
            </div>
            <div className="flex gap-0.5 flex-shrink-0">
              <button onClick={() => openUserEdit(u)} className="p-1.5 rounded-lg hover:bg-gray-100 text-[#6b6de3]"><Pencil size={14} /></button>
              <button onClick={() => { if (window.confirm('Silmek istediğinize emin misiniz?')) deleteUser(u.id).then(() => { toast.success('Silindi'); getUsers().then(r => setUsers(r.data)); }); }}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-[#FF3B30]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* User Modal */}
      <Dialog open={!!userModal} onOpenChange={o => !o && setUserModal(null)}>
        <DialogContent className="sm:rounded-2xl sm:max-w-md lg:max-w-3xl sm:max-h-[75vh] overflow-hidden flex flex-col p-4 sm:p-6">
          <DialogHeader><DialogTitle>{userModal?.id ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <div><Label className="text-xs">Ad Soyad</Label><Input value={userForm.name} onChange={e => setUserForm(p => ({ ...p, name: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <div><Label className="text-xs">Telefon</Label><Input type="tel" maxLength={11} value={userForm.phone} onChange={e => setUserForm(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 11) }))} placeholder="05XXXXXXXXX" className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <div><Label className="text-xs">Rol</Label>
              <Select value={userForm.role} onValueChange={v => setUserForm(p => ({ ...p, role: v }))}>
                <SelectTrigger className="h-12 bg-[#F2F2F7] border-0 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="clinic_owner">Klinik Sahibi</SelectItem>
                  <SelectItem value="clinic_vet">Veteriner</SelectItem>
                  <SelectItem value="clinic_tech">Teknisyen</SelectItem>
                  <SelectItem value="clinic_staff">Personel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-2 block">Klinikler (birden fazla seçilebilir)</Label>
              <div className="bg-[#F2F2F7] rounded-xl p-2 space-y-1 max-h-40 overflow-y-auto" data-testid="user-clinic-multiselect">
                {clinics.map(c => {
                  const selected = (userForm.clinic_ids || []).includes(c.id);
                  return (
                    <button key={c.id} type="button" onClick={() => toggleClinicInForm(c.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-2 ${selected ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      data-testid={`clinic-select-${c.id}`}>
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-white bg-white' : 'border-gray-300'}`}>
                        {selected && <div className="w-2 h-2 rounded-sm bg-[#6b6de3]" />}
                      </div>
                      {c.name}
                    </button>
                  );
                })}
              </div>
              {(userForm.clinic_ids || []).length > 1 && (
                <p className="text-[10px] text-[#6b6de3] mt-1 font-medium">{(userForm.clinic_ids || []).length} klinik seçili - kullanıcı panelde klinik değiştirebilir</p>
              )}
            </div>
            <div><Label className="text-xs">{userModal?.id ? 'Yeni Şifre (boş bırakırsanız değişmez)' : 'Şifre'}</Label><Input type="password" value={userForm.password} onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))} className="h-12 bg-[#F2F2F7] border-0 rounded-xl" /></div>
            <Button onClick={handleSaveUser} className="w-full bg-[#6b6de3] rounded-full">{userModal?.id ? 'Güncelle' : 'Oluştur'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
